/**
 * Reports · Sec 17(5) Ineligible-ITC Register — every purchase bill where the
 * tenant has reversed ITC under Sec 17(5) CGST Act. This maps into GSTR-3B
 * Table 4B(1) / 4D(1) at filing time.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileSpreadsheet, RefreshCw, ShieldAlert } from "lucide-react";

export default function IneligibleItcRegister() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/ineligible-itc-register",
        { params: { from_date: from || undefined, to_date: to || undefined } });
      setRows(r.data.rows || []);
      setTotal(r.data.total_reversed || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load 17(5) register");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dl = async () => {
    const fname = `Ineligible-ITC-${from || "all"}-to-${to || "all"}.xlsx`;
    await downloadBlob("/reports/ineligible-itc-register.xlsx", fname,
      { from_date: from || undefined, to_date: to || undefined });
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="ineligible-itc-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · SEC 17(5) BLOCKED CREDIT</div>
          <h1 className="text-3xl font-bold mt-1 font-heading flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-500" /> Ineligible ITC Register
          </h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Every purchase bill where you accepted the AI/rules advisory that ITC is
            blocked under Sec 17(5) CGST Act — motor vehicles, F&amp;B, works contract,
            personal use, gifts, composition supplies, etc. Feeds into GSTR-3B Table
            4B(1) at filing time.
          </div>
        </div>
        <div className="flex gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-40 rounded-sm h-9" data-testid="ineligible-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-40 rounded-sm h-9" data-testid="ineligible-to" />
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-sm gap-1.5" data-testid="ineligible-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={dl} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
            data-testid="ineligible-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="card-sharp p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">Bills</div>
          <div className="text-xl font-bold mt-1 font-mono tabular-nums">{rows.length}</div>
        </div>
        <div className="card-sharp p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">Total ITC reversed</div>
          <div className="text-xl font-bold mt-1 font-mono tabular-nums text-amber-500">{inr(total)}</div>
        </div>
        <div className="card-sharp p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">Clauses invoked</div>
          <div className="text-xl font-bold mt-1 font-mono tabular-nums">
            {new Set(rows.flatMap((r) => (r.reasons || []).map((x) => x.clause))).size}
          </div>
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="p-2 text-left">Bill No</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Vendor</th>
              <th className="p-2 text-left">17(5) Clauses</th>
              <th className="p-2 text-left">Reason (first line)</th>
              <th className="p-2 text-right">Reversed ₹</th>
              <th className="p-2 text-left">Reversed at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.bill_id} className="hover:bg-white/5">
                <td className="p-2 font-mono">{r.bill_number}</td>
                <td className="p-2 text-muted-foreground">{r.date}</td>
                <td className="p-2">{r.vendor_name}
                  <div className="text-[10px] font-mono text-muted-foreground">{r.vendor_gstin || "—"}</div>
                </td>
                <td className="p-2 font-mono text-amber-500">{r.clauses}</td>
                <td className="p-2 text-xs max-w-md">
                  {(r.reasons?.[0]?.reason || "").slice(0, 160)}
                </td>
                <td className="p-2 text-right font-mono tabular-nums text-amber-500">{inr(r.amount_reversed)}</td>
                <td className="p-2 text-[10px] font-mono text-muted-foreground">{r.reversal_at?.slice(0, 10) || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                No 17(5) reversals yet. When you save a purchase with a blocked-credit
                indication (motor vehicle, food, works contract, etc), the system
                flags it and you can accept or reject the AI verdict here.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
